import {
  File,
  FileArchive,
  FileAudio,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  type LucideIcon,
} from 'lucide-react';
import type { Nodo } from '@doc/shared';

export function tamano(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const unidades = ['KB', 'MB', 'GB', 'TB'];
  let valor = bytes / 1024;
  let i = 0;
  while (valor >= 1024 && i < unidades.length - 1) {
    valor /= 1024;
    i++;
  }
  return `${valor.toFixed(valor >= 10 || i === 0 ? 0 : 1)} ${unidades[i]}`;
}

const FORMATO_FECHA = new Intl.DateTimeFormat('es-CL', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function fecha(iso: string | null): string {
  if (!iso) return '—';
  return FORMATO_FECHA.format(new Date(iso));
}

export function fechaRelativa(iso: string | null): string {
  if (!iso) return '—';
  const minutos = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutos < 1) return 'recién';
  if (minutos < 60) return `hace ${minutos} min`;
  if (minutos < 60 * 24) return `hace ${Math.round(minutos / 60)} h`;
  if (minutos < 60 * 24 * 7) return `hace ${Math.round(minutos / 1440)} d`;
  return fecha(iso);
}

/** Icono y color según el tipo de archivo, como en cualquier explorador. */
export function iconoDe(nodo: Pick<Nodo, 'tipo' | 'mime' | 'extension'>): { Icono: LucideIcon; clase: string } {
  if (nodo.tipo === 'CARPETA') return { Icono: Folder, clase: 'carpeta' };
  const mime = nodo.mime ?? '';
  const ext = nodo.extension ?? '';
  if (mime.startsWith('image/')) return { Icono: FileImage, clase: 'archivo imagen' };
  if (mime.startsWith('video/')) return { Icono: FileVideo, clase: 'archivo imagen' };
  if (mime.startsWith('audio/')) return { Icono: FileAudio, clase: 'archivo imagen' };
  if (mime === 'application/pdf') return { Icono: FileText, clase: 'archivo pdf' };
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return { Icono: FileSpreadsheet, clase: 'archivo hoja' };
  if (['doc', 'docx', 'odt', 'txt', 'md', 'rtf'].includes(ext)) return { Icono: FileText, clase: 'archivo texto' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { Icono: FileArchive, clase: 'archivo' };
  if (['json', 'xml', 'yml', 'yaml', 'sql', 'html', 'css'].includes(ext)) return { Icono: FileCode, clase: 'archivo' };
  return { Icono: File, clase: 'archivo' };
}

export const iniciales = (nombre: string): string =>
  nombre
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

export const ETIQUETA_ROL: Record<string, string> = {
  SUPER_ADMIN: 'Super-admin',
  ADMIN: 'Administrador',
  MIEMBRO: 'Miembro',
};

export const ETIQUETA_ACCION: Record<string, string> = {
  LOGIN: 'Inicio de sesión',
  LOGIN_FALLIDO: 'Intento fallido',
  LOGOUT: 'Cierre de sesión',
  CARPETA_CREADA: 'Carpeta creada',
  ARCHIVO_SUBIDO: 'Archivo subido',
  VERSION_SUBIDA: 'Versión publicada',
  VERSION_RESTAURADA: 'Versión restaurada',
  DESCARGA: 'Descarga',
  RENOMBRADO: 'Renombrado',
  MOVIDO: 'Movido',
  COPIADO: 'Copiado',
  ELIMINADO: 'Enviado a papelera',
  RESTAURADO: 'Restaurado',
  PURGADO: 'Eliminado definitivamente',
  PERMISOS_CAMBIADOS: 'Permisos cambiados',
  HERENCIA_CAMBIADA: 'Herencia cambiada',
  CUOTA_FIJADA: 'Cuota de almacenamiento',
  ENLACE_CREADO: 'Enlace compartido',
  ENLACE_REVOCADO: 'Enlace revocado',
  ENLACE_VISITADO: 'Acceso por enlace',
  USUARIO_CREADO: 'Usuario creado',
  USUARIO_EDITADO: 'Usuario editado',
  GRUPO_CREADO: 'Grupo creado',
  GRUPO_EDITADO: 'Grupo editado',
  GRUPO_ELIMINADO: 'Grupo eliminado',
};
